// ============================================================
// Sponsored search overrides: when a shopper's search term CONTAINS one of
// these keywords (case-insensitive), that vendor's products are pinned first
// in the results, followed by all other matching products.
// The value must match the product's Vendor field in Shopify Admin exactly.
// If two keywords both match (e.g. "hair serum" and "serum"), the longer,
// more specific keyword wins.
// Add more { keyword: 'vendorName' } pairs here as new brands buy in.
const SPONSORED_SEARCH_OVERRIDES = {
  Shampoo: 'Agiva',
};
// ============================================================

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
  }

  connectedCallback() {
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.close();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results')) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && event.code !== 'Enter') {
      this.close();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')?.length) > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  // Returns the vendor to restrict results to, if the search term contains
  // a sponsored keyword — otherwise returns null.
  getSponsoredVendorOverride_(searchTerm) {
    const lowerTerm = searchTerm.toLowerCase();
    const matchedKeywords = Object.keys(SPONSORED_SEARCH_OVERRIDES)
      .filter((keyword) => lowerTerm.includes(keyword.toLowerCase()))
      .sort((a, b) => b.length - a.length); // longest = most specific wins
    return matchedKeywords.length ? SPONSORED_SEARCH_OVERRIDES[matchedKeywords[0]] : null;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Fields Shopify's predictive search will match the query against.
    // 'tag' is included so typing e.g. "serum" also surfaces products
    // that simply have a "serum" tag assigned in Admin, even if the
    // word doesn't appear in the title/product type.
    const searchFields = 'title,tag,product_type,variants.title,variants.sku,vendor';

    // Include every resource type Dawn shows by default (products,
    // collections, pages, articles, and query "Suggestions").
    const generalUrl =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&section_id=predictive-search` +
      `&resources[type]=product,collection,page,article,query` +
      `&resources[options][fields]=${searchFields}` +
      `&resources[options][unavailable_products]=last`;

    const sponsoredVendor = this.getSponsoredVendorOverride_(searchTerm);

    if (!sponsoredVendor) {
      this.fetchAndRender_(generalUrl, queryKey);
      return;
    }

    // Sponsored term: fetch the sponsored vendor's matches AND the normal
    // unfiltered matches in parallel, then merge — sponsored products are
    // pinned first, and every other matching product still shows after
    // them (nothing gets hidden, it's reordered rather than filtered out).
    const sponsoredUrl =
      `${routes.predictive_search_url}?q=${encodeURIComponent(`${searchTerm} vendor:"${sponsoredVendor}"`)}` +
      `&section_id=predictive-search` +
      `&resources[type]=product` +
      `&resources[options][fields]=${searchFields}` +
      `&resources[options][unavailable_products]=last` +
      `&resources[limit]=10`;

    Promise.all([
      fetch(sponsoredUrl, { signal: this.abortController.signal }).then((r) => (r.ok ? r.text() : '')),
      fetch(generalUrl, { signal: this.abortController.signal }).then((r) => (r.ok ? r.text() : '')),
    ])
      .then(([sponsoredText, generalText]) => {
        if (!generalText) {
          this.close();
          return;
        }
        const mergedMarkup = this.mergeSponsoredResults_(sponsoredText, generalText);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = mergedMarkup;
        });
        this.renderSearchResults(mergedMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  // Plain fetch-and-render for non-sponsored search terms (unchanged from
  // the original single-request flow).
  fetchAndRender_(url, queryKey) {
    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  // Merges the sponsored-vendor-only result set in front of the general
  // (unfiltered) result set. Sponsored products appear first, in their own
  // relevance order; every other matching product from the general search
  // follows after, deduplicated. Suggestions/Pages/Collections/Articles
  // come untouched from the general result.
  mergeSponsoredResults_(sponsoredText, generalText) {
    const parser = new DOMParser();

    const generalDoc = parser.parseFromString(generalText, 'text/html');
    const generalSection = generalDoc.querySelector('#shopify-section-predictive-search');
    if (!generalSection) return generalText;

    const sponsoredDoc = sponsoredText ? parser.parseFromString(sponsoredText, 'text/html') : null;

    // Product list items are identified generically by linking to "/products/"
    // — this avoids depending on any specific class name that might differ
    // across theme versions.
    const getProductItems = (root) =>
      root ? Array.from(root.querySelectorAll('li')).filter((li) => li.querySelector('a[href*="/products/"]')) : [];

    const getProductKey = (li) => {
      const link = li.querySelector('a[href*="/products/"]');
      return link ? link.getAttribute('href').split('?')[0] : null;
    };

    const sponsoredItems = getProductItems(sponsoredDoc);
    const generalItems = getProductItems(generalSection);

    if (!sponsoredItems.length || !generalItems.length) {
      return generalSection.innerHTML;
    }

    const productList = generalItems[0].closest('ul');
    if (!productList) return generalSection.innerHTML;

    const sponsoredKeys = new Set(sponsoredItems.map(getProductKey).filter(Boolean));
    const remainingItems = generalItems.filter((li) => !sponsoredKeys.has(getProductKey(li)));

    productList.innerHTML = '';
    sponsoredItems.forEach((li) => productList.appendChild(li.cloneNode(true)));
    remainingItems.forEach((li) => productList.appendChild(li));

    return generalSection.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
